import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faXmark, faPlug, faHeadset, faCalendarCheck, faSpinner, faKey, faWandMagicSparkles, faBookOpen, faSlidersH, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import ProviderHub from '../components/ProviderHub.js';
import CredentialsManager from '../components/CredentialsManager.js';
import { PROVIDER_META } from '../components/providerMeta.js';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import useAppBindings from '../hooks/useAppBindings.js';
import './ChannelsPage.css';
import './AppsPage.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// Connected platform apps expose their account and navigation in a compact panel.
// Wix knowledge lives in Knowledge Base; live capability previews stay in Apps.
const CONNECTION_ONLY_APPS = {
  wix: '/knowledge/wix',
  shopify: '/knowledge/shopify',
};

// Reused from the Activity → Inbox filter chips: the SVG border that draws on
// when a chip becomes active (styled by `.platform-filter-border-*`).
function FilterBorderDraw() {
  return (
    <svg className="platform-filter-border-svg" aria-hidden="true" focusable="false">
      <rect className="platform-filter-border-rect" pathLength="100" />
    </svg>
  );
}

// Warm the browser cache for the app logos at startup (this module is imported
// eagerly by App.js) so they paint instantly when the grid mounts, instead of
// each /public PNG being fetched only once its card renders — which makes the
// logos pop in a beat late on first load. Deferred to idle so it never competes
// with initial render.
if (typeof window !== 'undefined') {
  const warmAppLogos = () =>
    Object.values(PROVIDER_META).forEach((m) => {
      if (m && m.logo) {
        const img = new Image();
        img.src = m.logo;
      }
    });
  if (window.requestIdleCallback) window.requestIdleCallback(warmAppLogos);
  else setTimeout(warmAppLogos, 200);
}

// Logo (or fallback glyph) for an app, reusing the same /public assets the
// skill builder uses. Mirrors ChannelsPage's ChannelIcon.
export function AppIcon({ app }) {
  const meta = PROVIDER_META[app.key];
  if (meta && meta.logo) return <img src={meta.logo} alt="" />;
  return <FontAwesomeIcon icon={faPlug} className="ch-glyph" style={{ color: '#5616ea' }} />;
}

// Modal body for a webhook_only automation platform (Zapier / Make / n8n): there
// is no connect flow — a skill reaches it through an http tool with the owner's
// webhook URL. Explains what works (the catalog's can/cannot lists, sent verbatim
// by /api/integrations, same single source as the build agent's manifest) and
// hands off to the Agent-Studio builder with a prefilled request (the Studio
// composer reads its draft from localStorage).
function WebhookAppInfo({ app, onBuild }) {
  const { t } = useTranslation();
  const label = (PROVIDER_META[app.key] && PROVIDER_META[app.key].label) || app.label;
  return (
    <div className="webhook-app-info">
      <p className="webhook-app-summary">{app.summary}</p>
      {(app.can || []).length > 0 && (
        <div className="webhook-app-block">
          <h4>{t('integrations_can_do_heading')}</h4>
          <ul>{app.can.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {(app.cannot || []).length > 0 && (
        <div className="webhook-app-block">
          <h4>{t('integrations_cannot_do_heading')}</h4>
          <ul>{app.cannot.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      <p className="webhook-app-hint">{t('apps_webhook_hint', 'No account connection needed. Tell the build agent what to send and paste your webhook URL when it asks.')}</p>
      <button type="button" className="channels-help-btn" onClick={onBuild}>
        <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('integrations_connect_provider', { provider: label })}
      </button>
    </div>
  );
}

// Keep installation management on Shopify; Wix offers knowledge and skill previews.
export function ConnectionOnlyPanel({ app, botId, onLeave, initialDimension }) {
  const { t } = useTranslation();
  const [exploring, setExploring] = useState(app.key === 'wix' && Boolean(initialDimension));
  const navigate = useNavigate();
  const bindings = useAppBindings(botId);
  const meta = PROVIDER_META[app.key] || {};
  const label = meta.label || app.label;
  const account = app.key === 'shopify'
    ? (bindings.shopify_shop || (app.connected && app.connected.display_name) || label)
    : ((app.connected && app.connected.display_name) || label);
  const manageUrl = app.key === 'shopify' ? bindings.shopify_manage_url : bindings.wix_manage_url;

  if (exploring) return <div className="apps-wix-explore">
    <button type="button" className="phub-back" onClick={() => setExploring(false)}>
      <FontAwesomeIcon icon={faArrowRight} rotation={180} /> {t('wix_back_to_connection')}
    </button>
    <ProviderHub provider="wix" app={app} botId={botId} initialDimension={initialDimension} />
  </div>;

  return (
    <div className={`apps-conn${app.key === 'wix' ? ' apps-conn-wix' : ''}`}>
      <div className="apps-conn-identity">
        <span className="apps-conn-mark"><AppIcon app={app} /></span>
        <span className="apps-conn-account">{account}</span>
        <span className="apps-conn-status">
          <span className="apps-conn-dot" />
          {t('apps_conn_connected')}
        </span>
      </div>

      <div className="apps-conn-actions">
        <button
          type="button"
          className="apps-conn-action apps-conn-action-primary"
          onClick={() => { onLeave(); navigate(CONNECTION_ONLY_APPS[app.key]); }}
        >
          <span className="apps-conn-action-icon"><FontAwesomeIcon icon={faBookOpen} /></span>
          <span className="apps-conn-action-text">
            <span className="apps-conn-action-title">{app.key === 'wix' ? t('wix_manage_knowledge') : t('apps_view_in_knowledge', { app: label })}</span>
            <span className="apps-conn-action-sub">{app.key === 'wix' ? t('wix_manage_knowledge_sub') : t('apps_view_in_knowledge_sub')}</span>
          </span>
          <span className="apps-conn-action-go"><FontAwesomeIcon icon={faArrowRight} /></span>
        </button>

        {app.key === 'wix' ? <button type="button" className="apps-conn-action apps-wix-capabilities-action" onClick={() => setExploring(true)}>
          <span className="apps-conn-action-icon"><FontAwesomeIcon icon={faWandMagicSparkles} /></span>
          <span className="apps-conn-action-text">
            <span className="apps-conn-action-title">{t('wix_explore_capabilities')}</span>
            <span className="apps-conn-action-sub">{t('wix_explore_capabilities_sub')}</span>
          </span>
          <span className="apps-conn-action-go"><FontAwesomeIcon icon={faArrowRight} /></span>
        </button> : <button
          type="button"
          className="apps-conn-action"
          disabled={!manageUrl}
          onClick={() => manageUrl && window.open(manageUrl, '_blank', 'noopener,noreferrer')}
        >
          <span className="apps-conn-action-icon"><FontAwesomeIcon icon={faSlidersH} /></span>
          <span className="apps-conn-action-text">
            <span className="apps-conn-action-title">{t('apps_manage_on_platform', { app: label })}</span>
            <span className="apps-conn-action-sub">{t('apps_manage_on_platform_sub', { app: label })}</span>
          </span>
          <span className="apps-conn-action-go"><FontAwesomeIcon icon={faArrowUpRightFromSquare} /></span>
        </button>}
      </div>
    </div>
  );
}

// The detail modal of one app (also used by the "Apps" tab of the Agent-Studio panel):
// webhook apps explain the build flow, connected Wix/Shopify show their account, every
// other app opens <ProviderHub> (connect / manage / dimensions). Closes only via the X,
// so in-progress work isn't lost. `onWebhookBuild(label)` starts the builder request.
export function AppDetailModal({ app, botId, initialDimension = null, onClose, onWebhookBuild }) {
  const { t } = useTranslation();
  // Smoothly animate the modal's height as its content changes (detail load,
  // connect → connected, manage view). Copied from ChannelsPage.
  const morphRef = useRef(null);
  const [morphHeight, setMorphHeight] = useState(null);
  useEffect(() => {
    const el = morphRef.current;
    if (!el) return undefined;
    let raf = 0;
    const measure = () => {
      if (!morphRef.current) return;
      const h = morphRef.current.offsetHeight;
      setMorphHeight((prev) => (prev != null && Math.abs(prev - h) < 1 ? prev : h));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [app]);
  useBodyScrollLock();

  const label = (PROVIDER_META[app.key] && PROVIDER_META[app.key].label) || app.label;
  // A connected connection-only app shows just the bound account + one CTA, so
  // the modal drops back to a normal dialog width — the 1240px is there for the
  // ProviderHub's dimension grid and preview tables, which no longer render here.
  const connectionOnly = !app.webhook_only && CONNECTION_ONLY_APPS[app.key] && app.connected;

  return createPortal(
    <div className="channel-modal-overlay">
      <div className={`channel-modal channel-modal-apps${connectionOnly ? ' channel-modal-apps-narrow' : ''}${app.key === 'wix' ? ' channel-modal-wix' : ''}`}
        role="dialog" aria-label={label}>
        <div className="channel-modal-head">
          <span className="channel-modal-icon"><AppIcon app={app} /></span>
          <span className="channel-modal-title">{label}</span>
          <button type="button" className="channel-modal-close" onClick={onClose} aria-label={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="channel-modal-body">
          <div className="channel-modal-morph" style={{ height: morphHeight != null ? morphHeight : 'auto' }}>
            <div ref={morphRef}>
              {app.webhook_only ? (
                <WebhookAppInfo app={app} onBuild={() => onWebhookBuild && onWebhookBuild(label)} />
              ) : connectionOnly ? (
                <ConnectionOnlyPanel key={`${app.key}-${botId}`} initialDimension={initialDimension} app={app}
                  botId={botId} onLeave={onClose} />
              ) : (
                <ProviderHub provider={app.key} app={app} botId={botId} initialDimension={initialDimension} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// The workspace's stored API credentials (also reachable from the Agent-Studio "Apps" tab).
export function CredentialsModal({ onClose }) {
  const { t } = useTranslation();
  useBodyScrollLock();
  return createPortal(
    <div className="channel-modal-overlay" onClick={onClose}>
      <div className="channel-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('cred_title')}>
        <div className="channel-modal-head">
          <span className="channel-modal-icon"><FontAwesomeIcon icon={faKey} style={{ color: '#5616ea' }} /></span>
          <span className="channel-modal-title">{t('cred_title')}</span>
          <button type="button" className="channel-modal-close" onClick={onClose} aria-label={t('close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="channel-modal-body"><CredentialsManager /></div>
      </div>
    </div>,
    document.body
  );
}

// Lock background page scroll while a modal is open (it scrolls inside `.channel-modal-overlay`).
function useBodyScrollLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
}

/** The app catalog with connection state (`GET /api/integrations`, Wix scoped to the agent). */
export function useIntegrationProviders(botId) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    try {
      const url = `${API_URL}integrations${botId ? `?bot_id=${encodeURIComponent(botId)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = res.ok ? await res.json() : {};
      setProviders(Array.isArray(data.providers) ? data.providers : []);
    } catch {
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [botId]);
  useEffect(() => { refresh(); }, [refresh]);
  return { providers, loading, refresh };
}

// Apps (third-party integrations) rendered with the exact Channels look: one
// card per app, click to open a modal. The modal hosts <ProviderHub>, which shows
// the provider's catalog `dimensions` (products, services, events, …) when it
// declares any and is connected, and otherwise falls back to the <Integrations>
// connect / manage detail (OAuth, params, disconnect).
//
// Connected Wix uses a two-level panel; other connected platforms keep their account view.
function AppsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Wix is connected per-agent, so the Apps grid reflects the selected bot.
  const { selectedBotId } = useSelectedBot();
  const { providers, loading, refresh: fetchProviders } = useIntegrationProviders(selectedBotId);
  const [active, setActive] = useState(null);
  const [initialDimension, setInitialDimension] = useState(null);   // deep-link: drill straight into a hub dimension
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [showCreds, setShowCreds] = useState(false);   // workspace API-credentials manager

  // Filter by the catalog category the backend sends per provider (Scheduling,
  // CRM, Payments, …). Derived from the loaded apps in first-seen order — the
  // backend's deliberate DISPLAY_ORDER (Wix + Calendly first, then the
  // automation platforms) — so new categories appear automatically without a
  // code change.
  const categories = [];
  for (const p of providers) {
    if (p.category && !categories.includes(p.category)) categories.push(p.category);
  }
  const appFilters = [
    { key: 'all', label: t('filter_all') },
    ...categories.map((c) => ({ key: c, label: c })),
  ];

  const appCounts = { all: providers.length };
  categories.forEach((c) => {
    appCounts[c] = providers.filter((p) => p.category === c).length;
  });

  // Coming-soon apps (not connectable yet) always sort to the END of the grid,
  // keeping the usable ones first. Stable sort preserves the backend's
  // DISPLAY_ORDER within each group (Wix + Calendly, automation platforms,
  // remaining apps; coming-soon tail likewise).
  const byCategory = (filter === 'all' ? providers : providers.filter((p) => p.category === filter))
    .slice()
    .sort((a, b) => (a.coming_soon ? 1 : 0) - (b.coming_soon ? 1 : 0));
  // Free-text search layered on top of the category filter (matches the app
  // label + its summary).
  const q = query.trim().toLowerCase();
  const visibleProviders = q
    ? byCategory.filter((p) => {
        const meta = PROVIDER_META[p.key];
        const label = ((meta && meta.label) || p.label || '').toLowerCase();
        return label.includes(q) || (p.summary || '').toLowerCase().includes(q);
      })
    : byCategory;

  // Deep-link: arriving with ?app=<key>&dimension=<dim> (e.g. from the skill
  // builder's "View products" link) opens that provider's modal and drills the hub
  // straight into the dimension. Consume the params so closing won't re-trigger.
  useEffect(() => {
    const appKey = searchParams.get('app');
    if (!appKey || !providers.length) return;
    const target = providers.find((p) => p.key === appKey);
    const dimension = searchParams.get('dimension') || null;
    // Stored-source deep links open Knowledge Base; live Wix previews stay in this modal.
    if (target && !target.coming_soon && CONNECTION_ONLY_APPS[appKey] && target.connected &&
        (appKey !== 'wix' || ['products', 'site_content'].includes(dimension))) {
      setSearchParams({}, { replace: true });
      navigate(`${CONNECTION_ONLY_APPS[appKey]}${dimension ? `?dimension=${encodeURIComponent(dimension)}` : ''}`);
      return;
    }
    if (target && !target.coming_soon) {
      setActive(target);
      setInitialDimension(dimension);
    }
    setSearchParams({}, { replace: true });
  }, [providers, searchParams, setSearchParams, navigate]);

  // Re-sync the grid (connected badges) when the modal closes.
  const closeModal = useCallback(() => {
    setActive(null);
    setInitialDimension(null);
    fetchProviders();
  }, [fetchProviders]);

  return (
    <div className="channels-page">
      <header className="channels-head">
        <h1 className="channels-title">{t('apps_page_title')}</h1>
        <p className="channels-sub">
          {t('apps_page_sub')}
        </p>
        <button
          type="button"
          className="apps-credentials-btn"
          onClick={() => setShowCreds(true)}
        >
          <FontAwesomeIcon icon={faKey} /> {t('cred_manage_button')}
        </button>
      </header>

      {!loading && providers.length > 0 && (
        // Filter chips on the left, search on the right. `no-undraw` stays on
        // permanently: single-select filter, so only the newly active chip
        // should draw its border (no stray un-draw on the chips that were never
        // selected).
        <div className="channels-toolbar">
          <div className="platform-filters channels-filters no-undraw">
            {appFilters.map((f) => {
              const isActive = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className={`platform-filter ${isActive ? 'active' : ''}`}
                  onClick={() => setFilter(f.key)}
                  aria-pressed={isActive}
                >
                  <span className="platform-filter-count">{appCounts[f.key]}</span>
                  {f.label}
                  <FilterBorderDraw />
                </button>
              );
            })}
          </div>
          <TextField
            size="small"
            placeholder={t('apps_search_placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="channels-search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#9a93b3' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
                '& fieldset': { borderColor: '#e6e0f5' },
                '&:hover fieldset': { borderColor: '#c4b5fd' },
                '&.Mui-focused fieldset': { borderColor: '#5616ea', borderWidth: '1px' },
              },
              '& .MuiOutlinedInput-input': { fontSize: '0.85rem' },
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="apps-grid-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      ) : visibleProviders.length === 0 ? (
        <p className="apps-filter-empty">
          {q ? t('apps_no_match', { query }) : t('apps_none')}
        </p>
      ) : (
        <div className="channels-grid">
          {visibleProviders.map((app) => {
            const meta = PROVIDER_META[app.key];
            const comingSoon = !!app.coming_soon;
            return (
              <button
                key={app.key}
                type="button"
                className={`channel-card${comingSoon ? ' channel-card-soon' : ''}`}
                onClick={comingSoon ? undefined : () => setActive(app)}
                disabled={comingSoon}
                aria-disabled={comingSoon}
              >
                <span className="channel-card-icon">
                  <AppIcon app={app} />
                </span>
                <span className="channel-card-name">
                  {(meta && meta.label) || app.label}
                  {comingSoon ? (
                    <span className="channel-card-badge app-badge-soon">{t('apps_coming_soon', 'Coming soon')}</span>
                  ) : app.connected && (
                    <span className="channel-card-badge app-badge-connected">{t('studio_hero_apps_connected')}</span>
                  )}
                </span>
                <span className="channel-card-desc">{app.summary}</span>
                {/* webhook_only automation cards (never connected) fall through
                    to the standard "Connect" CTA — the modal then explains the
                    webhook flow and starts the build conversation. */}
                <span className="channel-card-cta">
                  {comingSoon
                    ? t('apps_coming_soon', 'Coming soon')
                    : <>{app.connected ? t('manage') : t('connect')} <FontAwesomeIcon icon={faArrowRight} /></>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!loading && (
      <div className="channels-help">
        <div className="channels-help-header">
          <FontAwesomeIcon icon={faHeadset} />
          <h3>{t('apps_help_title')}</h3>
        </div>
        <p>
          <Trans i18nKey="apps_help_body_html" components={{ strong: <strong /> }} />
        </p>
        <button
          type="button"
          className="channels-help-btn"
          onClick={() =>
            window.open(
              'https://calendly.com/build-ultimo-bots/integration_support',
              '_blank'
            )
          }
        >
          <FontAwesomeIcon icon={faCalendarCheck} />
          {t('book_integration_call')}
        </button>
      </div>
      )}

      {active && (
        <AppDetailModal
          app={active}
          botId={selectedBotId}
          initialDimension={initialDimension}
          onClose={closeModal}
          onWebhookBuild={(label) => {
            // hand off to the Agent-Studio builder with a prefilled request
            try {
              localStorage.setItem(`agentStudio.draft.${selectedBotId}`, t('agent_studio_webhook_draft', { app: label }));
              localStorage.setItem(`agentStudio.mode.${selectedBotId}`, 'build');
            } catch (_) { /* storage unavailable: the Studio still opens */ }
            navigate('/agent-studio/skills');
          }}
        />
      )}

      {showCreds && <CredentialsModal onClose={() => setShowCreds(false)} />}
    </div>
  );
}

export default AppsPage;
